<script setup>
import { reactive, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { required, email as emailValidator } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import { login } from '@/api/auth'
import FormInput from '@/components/Form/Input.vue'
import Button from '@/components/Button.vue'

const { t } = useI18n()

const credentials = reactive({
  email: '',
  password: '',
})

const loginApi = reactive({
  message: '',
  showLoading: false,
  hasErrored: false,
})

const rules = computed(() => ({
  email: { required, email: emailValidator },
  password: { required },
}))

const v$ = useVuelidate(rules, credentials)

const showAlertMessage = message => {
  loginApi.showLoading = false
  loginApi.message = message
}

const submitLogin = () => {
  loginApi.hasErrored = false
  loginApi.message = ''
  loginApi.showLoading = true

  login({ email: credentials.email, password: credentials.password }).catch(
    error => {
      loginApi.hasErrored = true
      showAlertMessage(error?.message || t('LOGIN.API.UNAUTH'))
    }
  )
}

const submitFormLogin = () => {
  if (v$.value.email.$invalid) {
    loginApi.hasErrored = true
    showAlertMessage(t('LOGIN.EMAIL.ERROR'))
    return
  }

  submitLogin()
}
</script>

<template>
  <main
    class="flex flex-col w-full min-h-screen py-20 bg-n-brand/5 dark:bg-n-background sm:px-6 lg:px-8"
  >
    <section class="max-w-5xl mx-auto">
      <h2 class="mt-6 text-3xl font-medium text-center text-n-slate-12">
        {{ $t('LOGIN.TITLE') }}
      </h2>
    </section>

    <section
      class="bg-white shadow sm:mx-auto mt-11 sm:w-full sm:max-w-lg dark:bg-n-solid-2 p-11 sm:shadow-lg sm:rounded-lg"
      :class="{ 'animate-wiggle': loginApi.hasErrored }"
    >
      <div
        v-if="loginApi.hasErrored && loginApi.message"
        class="mb-5 rounded-md bg-n-ruby-3 px-4 py-3 text-sm text-n-ruby-11"
      >
        {{ loginApi.message }}
      </div>
      <form class="space-y-5" @submit.prevent="submitFormLogin">
        <FormInput
          v-model="credentials.email"
          name="email_address"
          type="text"
          data-testid="email_input"
          :tabindex="1"
          required
          :label="$t('LOGIN.EMAIL.LABEL')"
          :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
          :has-error="v$.email.$error"
          @input="v$.email.$touch"
        />
        <FormInput
          v-model="credentials.password"
          type="password"
          name="password"
          data-testid="password_input"
          required
          :tabindex="2"
          :label="$t('LOGIN.PASSWORD.LABEL')"
          :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
          :has-error="v$.password.$error"
          @input="v$.password.$touch"
        />
        <Button
          lg
          justify="center"
          type="submit"
          data-testid="submit_button"
          class="w-full"
          :tabindex="3"
          :label="$t('LOGIN.SUBMIT')"
          :disabled="loginApi.showLoading"
        />
      </form>
    </section>
  </main>
</template>
